import React, { useState, useEffect, useMemo } from 'react';
import Layout, { PageKey } from '../components/Layout';
import {
  getWatchlist, addToWatchlist, removeFromWatchlist,
  setWatchlistCampaignVisibility, getWatchlistCampaigns, getAdAccounts,
} from '../api';

interface Props {
  user: { email: string; name: string };
  onLogout: () => void;
  onNavigate: (page: PageKey) => void;
  onOpenAccount: (accountId: string, accountName: string) => void;
}

interface WatchAccount { account_id: string; name: string; last_check: string; hidden_campaigns: string[]; }
interface Campaign {
  campaign_id: string; name: string; since: string; hidden: boolean;
  leads: number; spend: number; cpl: number | null; ctr: number; impressions: number; clicks: number;
}
interface CampState { campaigns: Campaign[]; last_check: string; loading: boolean; error: string; }

const nf = (n: number) => Math.round(n || 0).toLocaleString('de-DE');
const eur = (n: number | null | undefined) =>
  n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (s: string) => {
  if (!s) return '–';
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  } catch { return s; }
};
const fmtDateTime = (s: string) => {
  if (!s) return 'noch nie';
  try {
    const d = new Date(s);
    return `${fmtDate(s)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return s; }
};

const isNumeric = (s?: string) => !!s && /^\d+$/.test(s.trim());
function resolvedName(a: any): string {
  if (a.business_name?.trim() && !isNumeric(a.business_name)) return a.business_name.trim();
  if (a.page_name?.trim()) return a.page_name.trim();
  if (a.name?.trim() && !isNumeric(a.name)) return a.name.trim();
  return (a.name || a.id || '').replace('act_', '');
}

export default function Watchlist({ user, onLogout, onNavigate, onOpenAccount }: Props) {
  const [accounts, setAccounts] = useState<WatchAccount[]>([]);
  const [camps, setCamps] = useState<Record<string, CampState>>({});
  const [allAccounts, setAllAccounts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState<Record<string, boolean>>({});

  const loadWatchlist = () =>
    getWatchlist().then(r => setAccounts(r.data.accounts || [])).catch(() => {});

  useEffect(() => {
    loadWatchlist();
    getAdAccounts().then(r => setAllAccounts(r.data.accounts || [])).catch(() => {});
  }, []);

  // Kampagnen für neu hinzugekommene Partner laden
  useEffect(() => {
    accounts.forEach(a => {
      if (!camps[a.account_id]) loadCampaigns(a.account_id);
    });
    // eslint-disable-next-line
  }, [accounts]);

  const loadCampaigns = (aid: string) => {
    setCamps(c => ({ ...c, [aid]: { campaigns: c[aid]?.campaigns || [], last_check: c[aid]?.last_check || '', loading: true, error: '' } }));
    getWatchlistCampaigns(aid)
      .then(r => setCamps(c => ({ ...c, [aid]: { campaigns: r.data.campaigns || [], last_check: r.data.last_check || '', loading: false, error: '' } })))
      .catch(e => setCamps(c => ({
        ...c,
        [aid]: {
          campaigns: c[aid]?.campaigns || [], last_check: c[aid]?.last_check || '', loading: false,
          error: e.response?.status === 503 ? 'Daten gerade nicht verfügbar (Meta gedrosselt)' : 'Fehler beim Laden',
        },
      })));
  };

  const add = async (acc: any) => {
    setSearch('');
    await addToWatchlist(acc.id, resolvedName(acc));
    loadWatchlist();
  };
  const remove = async (aid: string) => {
    await removeFromWatchlist(aid);
    setAccounts(a => a.filter(x => x.account_id !== aid));
  };
  const toggleHide = async (aid: string, cid: string, hidden: boolean) => {
    await setWatchlistCampaignVisibility(aid, cid, hidden);
    setCamps(c => ({
      ...c,
      [aid]: { ...c[aid], campaigns: c[aid].campaigns.map(x => x.campaign_id === cid ? { ...x, hidden } : x) },
    }));
  };

  const watchedIds = new Set(accounts.map(a => a.account_id));
  const searchResults = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return [];
    return allAccounts
      .filter(a => !watchedIds.has(a.id) && resolvedName(a).toLowerCase().includes(q))
      .slice(0, 8);
    // eslint-disable-next-line
  }, [search, allAccounts, accounts]);

  return (
    <Layout user={user} onLogout={onLogout} activePage="watchlist" onNavigate={onNavigate}>
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>Watchlist</h1>
          <p style={s.subtitle}>Partner beobachten – aktive Kampagnen & Kennzahlen auf einen Blick</p>
        </div>

        {/* Suche */}
        <div style={{ position: 'relative' as const, maxWidth: 520, marginBottom: 28 }}>
          <div style={s.searchWrap}>
            <SearchIcon />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Partner suchen und hinzufügen…" style={s.searchInput}
            />
          </div>
          {searchResults.length > 0 && (
            <div style={s.dropdown}>
              {searchResults.map(a => (
                <div key={a.id} style={s.dropItem} onClick={() => add(a)}>
                  <span>{resolvedName(a)}</span>
                  <span style={s.addPlus}>+ hinzufügen</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {accounts.length === 0 && (
          <div style={{ textAlign: 'center' as const, padding: '60px 20px', color: '#6e6e73' }}>
            Noch keine Partner auf der Watchlist. Oben suchen und hinzufügen.
          </div>
        )}

        {accounts.map(a => {
          const cs = camps[a.account_id];
          const visible = (cs?.campaigns || []).filter(c => showHidden[a.account_id] || !c.hidden);
          const hiddenCount = (cs?.campaigns || []).filter(c => c.hidden).length;
          return (
            <div key={a.account_id} style={s.card}>
              <div style={s.cardHead}>
                <span style={s.partnerName} onClick={() => onOpenAccount(a.account_id, a.name)} title="Profil öffnen">
                  {a.name}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={s.lastCheck}>Letzter Check: {fmtDateTime(cs?.last_check || a.last_check)}</span>
                  <button style={s.refreshBtn} onClick={() => loadCampaigns(a.account_id)} title="Aktualisieren">
                    <RefreshIcon spinning={cs?.loading} />
                  </button>
                  <button style={s.removeBtn} onClick={() => remove(a.account_id)} title="Aus Watchlist entfernen">✕</button>
                </div>
              </div>

              {cs?.error ? (
                <div style={s.notice}>{cs.error}</div>
              ) : cs?.loading && !cs.campaigns.length ? (
                <div style={s.notice}>Lade Kampagnen…</div>
              ) : visible.length === 0 ? (
                <div style={s.notice}>Keine aktiven Kampagnen{hiddenCount ? ' (alle ausgeblendet)' : ''}.</div>
              ) : (
                <table style={s.table}>
                  <thead><tr>
                    {['Kampagne', 'Läuft seit', 'Leads', 'Ausgaben', 'CPL', 'CTR', ''].map((h, i) => (
                      <th key={h} style={{ ...s.th, textAlign: i === 0 ? 'left' : i === 6 ? 'center' : 'right' as any }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {visible.map((c, i) => (
                      <tr key={c.campaign_id} style={{ background: i % 2 ? '#fafafa' : 'transparent', opacity: c.hidden ? 0.5 : 1 }}>
                        <td style={s.tdName}>{c.name}</td>
                        <td style={s.td}>{fmtDate(c.since)}</td>
                        <td style={{ ...s.td, fontWeight: 700, color: c.leads > 0 ? '#1a3a5c' : '#aeaeb2' }}>{nf(c.leads)}</td>
                        <td style={s.td}>{eur(c.spend)}</td>
                        <td style={s.td}>{eur(c.cpl)}</td>
                        <td style={s.td}>{c.ctr.toFixed(2).replace('.', ',')} %</td>
                        <td style={{ ...s.td, textAlign: 'center' as const }}>
                          <button style={s.eyeBtn} onClick={() => toggleHide(a.account_id, c.campaign_id, !c.hidden)}
                            title={c.hidden ? 'Wieder einblenden' : 'Ausblenden'}>
                            {c.hidden ? '👁' : '🚫'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {hiddenCount > 0 && (
                <button style={s.showHiddenBtn} onClick={() => setShowHidden(h => ({ ...h, [a.account_id]: !h[a.account_id] }))}>
                  {showHidden[a.account_id] ? 'Ausgeblendete verbergen' : `${hiddenCount} ausgeblendete anzeigen`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}

function SearchIcon() {
  return <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#aeaeb2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
}
function RefreshIcon({ spinning }: { spinning?: boolean }) {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={spinning ? { animation: 'spin 0.7s linear infinite' } : {}}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>;
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: '40px 48px 72px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 800, color: '#1d1d1f', margin: 0, letterSpacing: '-0.5px' },
  subtitle: { margin: '6px 0 0', color: '#6e6e73', fontSize: 14 },

  searchWrap: { display: 'flex', alignItems: 'center', position: 'relative' as const },
  searchInput: {
    width: '100%', border: '1.5px solid #e8e8ed', borderRadius: 10, padding: '11px 14px 11px 38px',
    fontSize: 14, color: '#1d1d1f', outline: 'none', background: '#fff', boxSizing: 'border-box' as const,
  },
  dropdown: {
    position: 'absolute' as const, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff',
    border: '1px solid #e8e8ed', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 20, overflow: 'hidden',
  },
  dropItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px',
    cursor: 'pointer', fontSize: 13.5, color: '#1d1d1f', borderBottom: '1px solid #f5f5f7',
  },
  addPlus: { fontSize: 12, color: '#1a3a5c', fontWeight: 600 },

  card: { background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14, padding: '16px 20px', marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' as const, gap: 8 },
  partnerName: { fontSize: 16, fontWeight: 700, color: '#1d1d1f', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#d2d2d7', textUnderlineOffset: 3 },
  lastCheck: { fontSize: 11.5, color: '#aeaeb2' },
  refreshBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid #d2d2d7', background: '#fff', color: '#1a3a5c', cursor: 'pointer' },
  removeBtn: { width: 30, height: 30, borderRadius: 8, border: '1px solid #f0d0d0', background: '#fff', color: '#ff3b30', cursor: 'pointer', fontSize: 13 },

  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#aeaeb2', textTransform: 'uppercase' as const, letterSpacing: '0.05em', borderBottom: '1px solid #e8e8ed' },
  tdName: { padding: '9px 10px', color: '#1d1d1f', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  td: { padding: '9px 10px', color: '#1d1d1f', textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' },
  eyeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.8 },
  notice: { color: '#6e6e73', fontSize: 13, padding: '10px 0' },
  showHiddenBtn: { marginTop: 10, background: 'transparent', border: 'none', color: '#1a3a5c', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 },
};
